/**
 * Regenerate Options
 *
 * Shared helpers for assistant response regeneration.
 *
 * Primary responsibilities:
 * - Carry regenerate UI choices through the chat send path
 * - Convert "think longer" into a one-step reasoning increase
 *
 * Dependencies:
 * - Chat send option types
 */

import type { ChatSendOptions, ChatSettings } from '@/types/chat'
import type { ChatMessage } from '@/types/chat-message'

export type RegenerateMode = 'retry' | 'think' | 'web'

export type RegenerateReasoningEffort = Extract<
  NonNullable<ChatSettings['reasoningEffort']>,
  'minimal' | 'low' | 'medium' | 'high'
>

export interface RegenerateRequest {
  mode: RegenerateMode
  message?: string
  customInstruction?: string
  forceWebSearch?: boolean
  increaseReasoning?: boolean
  /** Override the model for this regeneration (e.g. lightweight model). */
  modelOverride?: string
}

export function getRegenerateMessage(
  request: RegenerateRequest | undefined,
  fallbackMessage?: string | null
) {
  return request?.message?.trim() || fallbackMessage?.trim() || ''
}

export function getPreviousUserMessageContent(messages: ChatMessage[]) {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index]?.message

    if (message?.role === 'user' && typeof message.content === 'string') {
      return message.content
    }
  }

  return null
}

export function getNextRegenerateReasoningEffort(
  current?: ChatSettings['reasoningEffort'] | null
): RegenerateReasoningEffort {
  if (current === 'high') return 'high'
  if (current === 'medium') return 'high'
  if (current === 'low') return 'medium'

  return 'low'
}

export function getRegenerateSendOptions(
  request: RegenerateRequest | undefined,
  chatSettings?: ChatSettings | null
): ChatSendOptions | undefined {
  if (!request) return undefined

  const options: ChatSendOptions = {}

  if (request.forceWebSearch) {
    options.forceWebSearch = true
  }

  if (request.increaseReasoning || request.modelOverride) {
    const override: Partial<ChatSettings> = {}

    if (request.increaseReasoning) {
      override.reasoningEffort = getNextRegenerateReasoningEffort(
        chatSettings?.reasoningEffort
      )
    }

    if (request.modelOverride) {
      override.model = request.modelOverride as ChatSettings['model']
    }

    options.chatSettingsOverride = override
  }

  return Object.keys(options).length > 0 ? options : undefined
}
